import { isNegativeDecimal, isZeroDecimal, parseDecimal, sumDecimals } from "@altek/ui/lib/decimal";
import { formatDecimalString } from "@altek/ui/lib/format/number";

/**
 * How a Stok screen writes a quantity: «2.000 kutu», «−400 kutu».
 *
 * THE RULE IS THE REPOSITORY'S: a quantity always renders with its unit of measure, and a
 * decimal string never becomes a JS number on the way to the screen. The warehouse-manager
 * review (7 October 2026) found the Stok tabs printing «2000», «600», «150» with no unit and no
 * thousands separator, and a document line printing the unit's CODE («KUTU-DEMO2») where a
 * person expects its name. Every Stok cell that shows an amount goes through this file now.
 *
 * Grouping and the decimal comma are the kit's `formatDecimalString` — one implementation of
 * the Turkish separators for the whole application, string in, string out.
 */

/** A unit as the API sends it beside a quantity: a product's `baseUom`, or a line's `uom`. */
export interface UnitRef {
  code?: string | null | undefined;
  name?: string | null | undefined;
}

const DASH = "—";

/**
 * The word that follows the number: the unit's NAME, never its code.
 *
 * A name written as a word is lower-cased the Turkish way because it follows a figure in running
 * text («Kutu» → «kutu», «İğne» → «iğne»). One written in capitals is an abbreviation and stays
 * exactly as written: lower-casing «IU» in Turkish gives «ıu», and «KG» is already right. The
 * code is used only when the unit has no name at all — a key is better than nothing, never
 * better than a word.
 */
export function unitWord(unit: UnitRef | null | undefined): string | null {
  const name = unit?.name?.trim();
  if (name) {
    return name === name.toLocaleUpperCase("tr-TR") ? name : name.toLocaleLowerCase("tr-TR");
  }
  const code = unit?.code?.trim();
  return code ? code : null;
}

/**
 * `"2000"` + Kutu → «2.000 kutu»; `"12.5"` + Kilogram → «12,5 kilogram».
 *
 * The precision is the server's: `"1.500"` stays «1,500» because trimming it would drop the
 * statement "measured to three places". A missing unit prints the bare figure rather than a
 * guessed word, and a missing value is a dash — never a «0» nobody measured.
 */
export function formatQuantity(value: string | null | undefined, unit?: UnitRef | null): string {
  if (value === null || value === undefined || value.trim() === "") return DASH;
  const figure = formatDecimalString(value);
  const word = unitWord(unit);
  return word ? `${figure} ${word}` : figure;
}

/**
 * `1`, `-1` or `0`, read from the decimal STRING.
 *
 * `Number("-0.000001")` happens to work; the rule exists because the cases where it does not
 * are the ones nobody tests. Anything that is not a decimal string reads as zero: a cell must
 * not paint an increase it cannot prove.
 */
export function quantitySign(value: string | null | undefined): -1 | 0 | 1 {
  const parsed = parseDecimal(value ?? null);
  if (parsed === null || isZeroDecimal(parsed)) return 0;
  return isNegativeDecimal(parsed) ? -1 : 1;
}

/**
 * Amounts of several articles, summed PER UNIT and written as one phrase: «1.200 kutu ve
 * 40 şişe» — what a shelf holds, said in a confirmation before somebody removes the shelf
 * (8 Ekim 2026).
 *
 * Boxes are never added to bottles: one total across units would be a number with no unit, the
 * thing `formatQuantity` exists to prevent. The sum is decimal arithmetic on the strings, in the
 * order the units first appear; an amount that is not a decimal string is left out rather than
 * read as zero.
 */
export function formatQuantitiesByUnit(
  items: ReadonlyArray<{ value: string | null | undefined; unit?: UnitRef | null | undefined }>,
): string {
  const groups = new Map<string, { unit: UnitRef | null | undefined; values: string[] }>();
  for (const item of items) {
    const value = parseDecimal(item.value ?? null);
    if (value === null) continue;
    const key = item.unit?.code ?? item.unit?.name ?? "";
    const group = groups.get(key) ?? { unit: item.unit, values: [] };
    group.values.push(value);
    groups.set(key, group);
  }
  const parts = [...groups.values()].map((group) =>
    formatQuantity(sumDecimals(group.values), group.unit),
  );
  if (parts.length <= 1) return parts[0] ?? DASH;
  return `${parts.slice(0, -1).join(", ")} ve ${parts[parts.length - 1] ?? ""}`;
}

/**
 * How many decimal places a quantity actually USES: `"12.5"` → 1, `"12.50"` → 1, `"12.0"` → 0,
 * `"12"` → 0. Null for a value that is not a decimal string.
 *
 * Trailing zeros do not count: «12,0 kutu» is twelve boxes, and a unit counted in whole numbers
 * (`decimalScale: 0`) has to accept it. This is the comparison a form makes against the unit's
 * own scale (8 Ekim 2026, «Sipariş Noktası» took «12,5» for boxes).
 */
export function usedDecimalPlaces(value: string | null | undefined): number | null {
  const parsed = parseDecimal(value ?? null);
  if (parsed === null) return null;
  const fraction = parsed.split(".")[1] ?? "";
  return fraction.replace(/0+$/, "").length;
}

/**
 * A ledger delta with its direction in front: «+1.200 kutu», «−400 kutu», «0 kutu».
 *
 * The minus is the typographic one (U+2212), so it never reads as a hyphen in a code, and the
 * sign is taken from the figure — never from the movement's name (`ADJUSTMENT_IN` can carry a
 * negative amount).
 */
export function formatSignedQuantity(
  delta: string | null | undefined,
  unit?: UnitRef | null,
): string {
  if (delta === null || delta === undefined || delta.trim() === "") return DASH;
  const sign = quantitySign(delta);
  const body = formatQuantity(delta.trim().replace(/^[+-]/, ""), unit);
  if (sign === 1) return `+${body}`;
  if (sign === -1) return `−${body}`;
  return body;
}
